import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

//const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";
const API_URL = process.env.REACT_APP_API_URL;

// Función auxiliar para obtener el token de autenticación del localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// Acciones asíncronas
export const fetchAnimes = createAsyncThunk('animes/fetchAnimes', async () => {
  try {
    const response = await axios.get(`${API_URL}/api/v1/animes`);
    console.log('Respuesta completa de la API:', response);
    console.log('Estructura de response.data:', response.data);
    
    // Verificar si response.data.data existe
    if (response.data.data) {
      console.log('Animes encontrados en response.data.data:', response.data.data);
      return response.data.data;
    } 
    // Si no existe data, pero hay un array en response.data, podría ser ese el array de animes
    else if (Array.isArray(response.data)) {
      console.log('Animes encontrados directamente en response.data:', response.data);
      return response.data;
    }
    // En caso de otras estructuras, devolver lo que haya en data
    else {
      console.log('Estructura de respuesta inesperada, devolviendo response.data:', response.data);
      return response.data;
    }
  } catch (error) {
    console.error('Error al obtener animes:', error);
    throw error;
  }
});

export const addAnime = createAsyncThunk('animes/addAnime', async (animeData) => {
  const authHeader = getAuthHeader();
  console.log('Enviando solicitud con cabeceras:', authHeader);
  const response = await axios.post(`${API_URL}/api/v1/animes`, animeData, authHeader);
  return response.data.data;
});

export const updateAnime = createAsyncThunk('animes/updateAnime', async ({ id, animeData }) => {
  const authHeader = getAuthHeader();
  const response = await axios.put(`${API_URL}/api/v1/animes/${id}`, animeData, authHeader);
  return response.data.data;
});

export const deleteAnime = createAsyncThunk('animes/deleteAnime', async (id, { rejectWithValue }) => {
  try {
    const authHeader = getAuthHeader();
    await axios.delete(`${API_URL}/api/v1/animes/${id}`, authHeader);
    return id;
  } catch (error) {
    // Capturar el mensaje de error del backend
    const errorMessage = error.response && error.response.data && error.response.data.message
      ? error.response.data.message
      : 'Error al eliminar el anime';
    return rejectWithValue({ message: errorMessage });
  }
});

// Slice
const animeSlice = createSlice({
  name: 'animes',
  initialState: {
    items: [],
    status: 'idle',
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Manejar fetchAnimes
      .addCase(fetchAnimes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAnimes.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Mapear los datos que vienen de la API al formato que espera el componente
        state.items = action.payload.map(anime => ({
          id: anime.id,
          name: anime.name || anime.title,  // Compatibilidad con ambos campos
          genre: Array.isArray(anime.genre) ? anime.genre.join(', ') : anime.genre,
          releaseYear: anime.releaseYear || anime.year,
          totalSeasons: anime.totalSeasons || anime.seasons?.length || 0,
          // Otros campos que puedan ser necesarios
          description: anime.description || anime.synopsis,
          poster: anime.poster || anime.image,
          // Mantener otros campos originales
          ...anime
        }));
      })
      .addCase(fetchAnimes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Manejar addAnime
      .addCase(addAnime.fulfilled, (state, action) => {
        const formattedAnime = {
          id: action.payload.id,
          name: action.payload.name || action.payload.title,
          genre: Array.isArray(action.payload.genre) ? action.payload.genre.join(', ') : action.payload.genre,
          releaseYear: action.payload.releaseYear || action.payload.year,
          totalSeasons: action.payload.totalSeasons || action.payload.seasons?.length || 0,
          description: action.payload.description || action.payload.synopsis,
          poster: action.payload.poster || action.payload.image,
          ...action.payload
        };
        state.items.push(formattedAnime);
      })
      // Manejar updateAnime
      .addCase(updateAnime.fulfilled, (state, action) => {
        const formattedAnime = {
          id: action.payload.id,
          name: action.payload.name || action.payload.title,
          genre: Array.isArray(action.payload.genre) ? action.payload.genre.join(', ') : action.payload.genre,
          releaseYear: action.payload.releaseYear || action.payload.year,
          totalSeasons: action.payload.totalSeasons || action.payload.seasons?.length || 0,
          description: action.payload.description || action.payload.synopsis,
          poster: action.payload.poster || action.payload.image,
          ...action.payload
        };
        const index = state.items.findIndex(anime => anime.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = formattedAnime;
        }
      })
      // Manejar deleteAnime
      .addCase(deleteAnime.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteAnime.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = state.items.filter(anime => anime.id !== action.payload);
      })
      .addCase(deleteAnime.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload.message;
      });
  }
});

export default animeSlice.reducer;
